import type { ComponentType } from 'react';
import type {
  PluginManifest,
  PluginManifestSecretStatus,
  PluginManifestSetupStep,
  PluginManifestTableColumn,
  PluginManifestTableSchema,
  PluginWebRegistration,
  PluginIconComponent,
  PluginNavItem,
  PluginCommand,
  PluginOverviewCard,
  PluginAgentRef,
  PluginWebManifest,
  PluginMarketingPreviewSurface,
  PluginNavUtilityProps,
  AdminUserActionProps,
  AuthProviderWebConfig,
  AuthProviderFormProps,
  SearchProviderProps,
  LaunchMentionProviderProps,
  ChatMentionProviderProps,
  LaunchMentionCreateProps,
  LaunchMentionEditProps,
  AgentContextProviderProps,
  FlowTriggerRendererProps,
  ToolCallRendererProps,
  ResourcePreviewProps,
  AttachmentSummaryProps,
  AssociationChipProps,
} from '@reflex/plugin-api';
import type { OrganizationDisabledReason, SessionSharingSetting } from '@reflex/shared';
import { CONTEXT_REFERENCE_KIND_PATTERN } from '@runloop/reflex-contract';

export type { PluginManifest } from '@reflex/plugin-api';
export type { PluginManifestHealthChecks } from '@reflex/plugin-api';
export type PluginSecretStatus = PluginManifestSecretStatus;
export type PluginSetupStep = PluginManifestSetupStep;
export type PluginTableColumn = PluginManifestTableColumn;
export type PluginTableSchema = PluginManifestTableSchema;
export type { PluginIconComponent };
export type IconComponent = PluginIconComponent;

// Auto-discover all plugin web registrations via glob.
// Each plugin with a web UI exports a src/web/register.ts module
// containing its lazy component imports and icon references.
//
// IMPORTANT — snapshot semantics. `import.meta.glob({ eager: true })` is a
// one-shot snapshot taken at module-init time. The `componentMap` built
// below is therefore frozen against whichever set of `register.ts` files
// existed when this module was first imported by the browser. When a
// plugin's `register.ts` later adds a new `componentPath` key (e.g. a new
// `marketingPreviews` entry), Vite HMR refreshes that file but does NOT
// re-execute this module — so the new key never appears in `componentMap`
// until the user hard-refreshes or the dev server restarts. Symptom:
// `[plugin-registry] N web wiring gap(s)` keeps firing for components that
// demonstrably exist on disk. The HMR handler at the bottom of this file
// detects key-set changes and forces a full page reload, and
// `reportWiringGapsOnce` in `usePlugins.ts` auto-recovers from
// already-stuck sessions with a one-shot reload guarded by sessionStorage.
//
// Two further non-obvious gotchas baked into this glob:
//   1. The path is `../../node_modules/...` (TWO levels up from
//      `web/src/lib/`), landing on `web/node_modules/...`. The earlier
//      revision used three levels and resolved against the repo root's
//      `node_modules/`, where the `@reflex` scope doesn't exist
//      (pnpm workspaces hoist scoped names into each consumer's own
//      `node_modules/`, not the root). The result was a glob that
//      silently matched zero files, leaving the registry empty and
//      tripping `[plugin-registry] N web wiring gap(s)` at boot.
//   2. `exhaustive: true` opts out of Vite's default
//      `**/node_modules/**` ignore for `import.meta.glob`. Without it
//      the matcher refuses to consider any `node_modules/...` path,
//      regardless of how many levels deep we are.
//
// We deliberately glob through `web/node_modules/@reflex/...`
// rather than the raw `plugins/...` workspace path so each plugin's
// transitive imports (`@reflex/plugin-api/create-web-registration`,
// etc.) resolve through the symlink tree pnpm built for `web/`'s
// declared dependencies. Loading via `plugins/<name>/...` directly
// bypasses that tree and Vite can't find peers like `react` from
// `plugins/plugin-api/`.
const registrationModules = import.meta.glob<{ default: PluginWebRegistration }>(
  '../../node_modules/@reflex/plugin-*/src/web/register.ts',
  { eager: true, exhaustive: true },
);

let componentMap: Record<string, ComponentType> = {};
let iconMap: Record<string, IconComponent> = {};
let authProviderMap: Record<string, AuthProviderWebConfig> = {};
let staticPublicRoutes: ResolvedPluginRoute[] = [];
let staticDocsPublicRoutes: ResolvedPluginRoute[] = [];
let staticChromelessPublicRoutes: ResolvedPluginRoute[] = [];
let staticHomePage: ComponentType | null = null;

function rebuildMaps() {
  const nextComponents: Record<string, ComponentType> = {};
  const nextIcons: Record<string, IconComponent> = {};
  const nextAuthProviders: Record<string, AuthProviderWebConfig> = {};
  for (const mod of Object.values(registrationModules)) {
    Object.assign(nextComponents, mod.default.components);
    Object.assign(nextIcons, mod.default.icons);
    if (mod.default.authProviders) {
      Object.assign(nextAuthProviders, mod.default.authProviders);
    }
  }
  componentMap = nextComponents;
  iconMap = nextIcons;
  authProviderMap = nextAuthProviders;

  // Build static public-route lists from each registration's bundled
  // manifest. Anonymous visitors hit these routes (marketing pages, docs
  // pages, replay share links) before the auth-gated
  // `GET /api/config/plugins` ever resolves, so we resolve them at
  // module-init time straight from the glob — no HTTP, no AuthGate
  // dependency.
  //
  // Docs-shell routes (carrying `docs` route metadata, PRD-8a / ADR-002)
  // are split into a separate list so `App.tsx` can mount them inside
  // `DocsLayout` instead of `MarketingLayout`. A `public: true` route
  // with neither `marketing` nor `docs` metadata defaults to the
  // marketing shell, preserving existing behaviour.
  const nextPublic: ResolvedPluginRoute[] = [];
  const nextDocsPublic: ResolvedPluginRoute[] = [];
  const nextChromelessPublic: ResolvedPluginRoute[] = [];
  for (const mod of Object.values(registrationModules)) {
    const manifest = mod.default.manifest;
    if (!manifest?.routes) continue;
    for (const route of manifest.routes) {
      if (!route.public) continue;
      const component = nextComponents[route.componentPath];
      if (!component) continue;
      const resolved: ResolvedPluginRoute = {
        path: route.path,
        component,
        config: route.config,
      };
      if (route.chromeless) {
        nextChromelessPublic.push(resolved);
      } else if (route.docs) {
        nextDocsPublic.push(resolved);
      } else {
        nextPublic.push(resolved);
      }
    }
  }
  staticPublicRoutes = nextPublic;
  staticDocsPublicRoutes = nextDocsPublic;
  staticChromelessPublicRoutes = nextChromelessPublic;

  // Logged-out home page: like the public routes above, anonymous visitors
  // render `/` before any authed config fetch, so the contribution resolves
  // statically from the bundled manifests. Highest `priority` wins so a
  // deployment-specific marketing plugin can override the stock page.
  let nextHome: { component: ComponentType; priority: number } | null = null;
  for (const mod of Object.values(registrationModules)) {
    const home = mod.default.manifest?.homePage;
    if (!home) continue;
    const component = nextComponents[home.componentPath];
    if (!component) continue;
    const priority = home.priority ?? 0;
    if (!nextHome || priority > nextHome.priority) {
      nextHome = { component, priority };
    }
  }
  staticHomePage = nextHome?.component ?? null;
}

/**
 * Look up a plugin-contributed component by its registration key.
 * Returns `undefined` if the component is not found (plugin not loaded or disabled).
 */
export function getPluginComponent(key: string): ComponentType | undefined {
  return componentMap[key];
}

/**
 * Look up a plugin-registered icon component by its registration name
 * (e.g. `"Trophy"`, `"UserCircle"`). Returns `undefined` when no plugin
 * exports an icon under that name. Unlike the internal {@link resolveIcon}
 * this never warns — callers (e.g. `LucideIconByName`) are expected to
 * provide their own fallback when the icon is absent.
 */
export function getPluginIcon(name: string): IconComponent | undefined {
  return iconMap[name];
}

export function getAuthProviderIcon(name: string): IconComponent | undefined {
  return authProviderMap[name]?.icon;
}

export function getAuthProviderBenefit(name: string): string | undefined {
  return authProviderMap[name]?.benefit;
}

/**
 * Resolve a password provider's self-contained sign-in / registration form
 * (e.g. the email/password form contributed by `plugin-auth-email`). Returns
 * `undefined` when the provider contributes no form — the host treats that as
 * "no email surface to render" and falls back to OAuth-only. See
 * {@link AuthProviderFormProps} for the contract the component receives.
 */
export function getAuthProviderForm(
  name: string,
): ComponentType<AuthProviderFormProps> | undefined {
  return authProviderMap[name]?.form;
}

/**
 * Public, non-chromeless routes (`public: true`) that target the marketing
 * shell — i.e. carry `marketing` metadata or no shell-specific metadata at
 * all. Used by `App.tsx` to mount marketing-style pages without depending
 * on the auth-gated `GET /api/config/plugins` fetch — anonymous visitors
 * must be able to render these immediately.
 */
export function getStaticPublicRoutes(): ResolvedPluginRoute[] {
  return staticPublicRoutes;
}

/**
 * Public docs routes (`public: true` + `docs` metadata) collected from every
 * plugin's bundled manifest. Used by `App.tsx` to mount docs pages inside
 * the docs-owned `DocsLayout` (PRD-8a / ADR-002) — a parallel branch to the
 * marketing shell that does not depend on `MarketingLayout`.
 */
export function getStaticDocsPublicRoutes(): ResolvedPluginRoute[] {
  return staticDocsPublicRoutes;
}

/**
 * Chromeless public routes (`public: true`, `chromeless: true`) collected
 * from every plugin's bundled manifest. Replay share links (`/replays/:id/live`)
 * and other full-viewport anonymous-viewer surfaces live here.
 */
export function getStaticChromelessPublicRoutes(): ResolvedPluginRoute[] {
  return staticChromelessPublicRoutes;
}

/**
 * The plugin-contributed logged-out home page (`PluginWebManifest.homePage`),
 * or `null` when no composed plugin contributes one. `App.tsx` renders this
 * at `/` for anonymous visitors; with no contribution the root falls back to
 * the sign-in screen (the pre-marketing behaviour).
 */
export function getStaticHomePage(): ComponentType | null {
  return staticHomePage;
}

rebuildMaps();

// Vite HMR gotcha that bit us in #1010 (and again when a new plugin global UI
// element was added): `import.meta.glob({ eager: true })` captures
// `componentMap` as a one-shot snapshot at module-init time. A plugin's
// `register.ts` is a glob dependency of this module, but its namespace inside
// `registrationModules` is NOT refreshed in place when Vite hot-updates it — so
// rebuilding the map from that snapshot here reads the STALE registration.
//
// The previous handler rebuilt the map and only reloaded when the (stale)
// rebuild reported an added/removed key. Because the snapshot never changed,
// adding a new `componentPath` produced no diff and was silently absorbed with
// no reload — leaving `componentMap` missing the new component until a manual
// dev-server restart (symptom: a persistent `[plugin-registry] N web wiring
// gap(s)` error for a component that demonstrably exists on disk; mounted
// `React.lazy()` wrappers also still point at old references).
//
// A full page reload re-runs the eager glob from scratch and is the only
// reliable way to pick up a changed registration, so always reload when this
// module or any plugin `register.ts` updates.
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    console.info('[plugin-registry] registration changed; reloading to rebuild the component map');
    location.reload();
  });
}

// Warnings are silenced under Vitest so stories that exercise mock/kitchen-sink
// plugin manifests (e.g. AdminPluginDetailPage) don't emit hundreds of lines of
// noise. The admin UI still surfaces missing registrations visibly through
// `collectPluginWebWiringIssues`.
const IS_TEST_ENV = import.meta.env?.MODE === 'test';

function resolveIcon(name: string): IconComponent | undefined {
  const icon = iconMap[name];
  if (!icon && !IS_TEST_ENV) {
    console.warn(
      `[plugin-registry] Icon "${name}" not found. Ensure the plugin's web/register.ts exports it in the icons map.`,
    );
  }
  return icon;
}

function resolveComponent(
  componentPath: string,
  context: string,
  options?: { quiet?: boolean },
): ComponentType | undefined {
  const component = componentMap[componentPath];
  if (!component && !IS_TEST_ENV && !options?.quiet) {
    // Resolvers run inside a `useMemo` that recomputes on many renders, so a
    // per-call `console.warn` for a genuinely-missing REQUIRED component is
    // still bounded and useful. OPTIONAL components (badge, attachment summary,
    // launch mention create) must pass `quiet` — they degrade silently and the
    // boot-time wiring collector already reports them exactly once, so logging
    // per render would flood the console (thousands of lines) for a
    // gracefully-handled gap.
    console.warn(
      `[plugin-registry] ${context} component "${componentPath}" not found. Ensure the plugin's web/register.ts exports it in the components map.`,
    );
  }
  return component;
}

function isComponentRegistered(componentPath: string): boolean {
  return componentPath !== '' && componentMap[componentPath] != null;
}

function isIconRegistered(name: string): boolean {
  return name !== '' && iconMap[name] != null;
}

type PluginWeb = NonNullable<PluginManifest['web']>;

/**
 * Iterates enabled plugin manifests, extracts items from each manifest's web
 * config, and applies a transform that resolves components/icons. Items where
 * the transform returns `null` (missing registration) are silently skipped.
 */
function resolveManifestItems<TInput, TOutput>(
  manifests: PluginManifest[],
  getItems: (web: PluginWeb) => TInput[],
  transform: (item: TInput, manifest: PluginManifest) => TOutput | null,
): TOutput[] {
  const result: TOutput[] = [];
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    const web = manifest.web;
    if (!web) continue;
    for (const item of getItems(web)) {
      const resolved = transform(item, manifest);
      if (resolved) result.push(resolved);
    }
  }
  return result;
}

/**
 * Lists manifest web keys that have no matching lazy component or icon in the
 * bundled plugin registration maps (used for admin diagnostics).
 */
type WiringFieldKey = keyof PluginWebManifest;

interface WiringCheck {
  field: WiringFieldKey;
  kind: string;
  idProp: string;
  componentProp?: string;
  optionalComponentProps?: readonly string[];
  iconProp?: string;
  /**
   * When set, the field is treated as the icon name but absent/empty values
   * are silently allowed (icon is optional per the manifest schema). The
   * registry only flags an explicitly-named icon that fails to resolve.
   */
  optionalIconProp?: string;
}

const WIRING_CHECKS: WiringCheck[] = [
  {
    field: 'routes',
    kind: 'route',
    idProp: 'path',
    componentProp: 'componentPath',
    // `marketing.icon` is validated separately below because it is nested; the
    // simple `iconProp` check would need to traverse one level deeper.
  },
  {
    field: 'navItems',
    kind: 'nav',
    idProp: 'label',
    iconProp: 'icon',
    optionalComponentProps: ['badge'],
  },
  { field: 'commands', kind: 'command', idProp: 'label', iconProp: 'icon' },
  { field: 'overviewCards', kind: 'overview card', idProp: 'label', iconProp: 'icon' },
  {
    field: 'agentActions',
    kind: 'agent action',
    idProp: 'label',
    iconProp: 'icon',
    optionalComponentProps: ['componentPath'],
  },
  {
    field: 'adminUserActions',
    kind: 'admin user action',
    idProp: 'id',
    componentProp: 'componentPath',
  },
  {
    field: 'agentTabs',
    kind: 'agent tab',
    idProp: 'id',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'pageTabs',
    kind: 'page tab',
    idProp: 'id',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'settingsSections',
    kind: 'settings section',
    idProp: 'id',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'orgSettingsSections',
    kind: 'org settings section',
    idProp: 'id',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'profileSections',
    kind: 'profile section',
    idProp: 'id',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'agentDetailSections',
    kind: 'agent detail section',
    idProp: 'id',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'agentHeaderBadges',
    kind: 'agent header badge',
    idProp: 'id',
    componentProp: 'componentPath',
  },
  {
    field: 'associationChips',
    kind: 'association chip',
    idProp: 'kind',
    componentProp: 'componentPath',
  },
  { field: 'homeWidgets', kind: 'home widget', idProp: 'id', componentProp: 'componentPath' },
  {
    field: 'attachments',
    kind: 'attachment',
    idProp: 'id',
    componentProp: 'pickerComponentPath',
    optionalComponentProps: ['summaryComponentPath'],
    iconProp: 'icon',
  },
  {
    field: 'searchProviders',
    kind: 'search provider',
    idProp: 'id',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'launchMentionProviders',
    kind: 'launch mention provider',
    idProp: 'id',
    componentProp: 'componentPath',
    optionalComponentProps: ['createComponentPath', 'editComponentPath'],
    iconProp: 'icon',
  },
  {
    field: 'agentContextProviders',
    kind: 'agent context provider',
    idProp: 'id',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'chatMentionProviders',
    kind: 'chat mention provider',
    idProp: 'id',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'flowTriggerRenderers',
    kind: 'automation trigger renderer',
    idProp: 'triggerType',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'toolCallRenderers',
    kind: 'tool call renderer',
    idProp: 'toolName',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'resourcePreviews',
    kind: 'resource preview',
    idProp: 'kind',
    componentProp: 'componentPath',
    iconProp: 'icon',
  },
  {
    field: 'contentScanners',
    kind: 'content scanner',
    idProp: 'id',
    componentProp: 'componentPath',
  },
  {
    field: 'marketingPreviews',
    kind: 'marketing preview',
    idProp: 'id',
    componentProp: 'componentPath',
  },
  {
    field: 'globalUiElements',
    kind: 'global ui element',
    idProp: 'id',
    componentProp: 'componentPath',
  },
  {
    field: 'navUtilityElements',
    kind: 'nav utility element',
    idProp: 'id',
    componentProp: 'componentPath',
  },
  // userSetupSteps have an optional icon and no component (href-based navigation)
  {
    field: 'userSetupSteps',
    kind: 'user setup step',
    idProp: 'id',
    optionalIconProp: 'icon',
  },
];

export interface PluginWebWiringIssue {
  /** Human-readable description of the missing registration. */
  message: string;
  /**
   * `true` for gaps in `optionalComponentProps` slots — a launch mention
   * provider's `createComponentPath`, an attachment's `summaryComponentPath`, a
   * nav item's `badge`, etc. Their resolvers already drop the feature when the
   * component is absent (see `resolveLaunchMentionProviders`), so a
   * stale/missing optional component must degrade quietly and MUST NOT brick
   * the boot. `false` marks a genuinely broken required registration worth
   * surfacing loudly.
   */
  optional: boolean;
}

/**
 * Structured variant of {@link collectPluginWebWiringIssues} that tags each gap
 * as required vs optional. The boot-time reporter in `usePlugins` uses this to
 * fail loudly on real breakage while letting optional features (an inline
 * "Add new …" row, a summary chip) degrade silently instead of crashing the app.
 */
export function collectPluginWebWiringIssuesDetailed(
  plugin: PluginManifest,
): PluginWebWiringIssue[] {
  const issues: PluginWebWiringIssue[] = [];
  const web = plugin.web;
  if (!web) return issues;
  const p = `Plugin "${plugin.name}"`;

  for (const check of WIRING_CHECKS) {
    const items = (web[check.field] ?? []) as unknown as Record<string, string>[];
    for (const item of items) {
      const id = item[check.idProp];
      if (check.componentProp && !isComponentRegistered(item[check.componentProp] ?? '')) {
        issues.push({
          message: `${p} ${check.kind} "${id}": missing component "${item[check.componentProp]}"`,
          optional: false,
        });
      }
      for (const prop of check.optionalComponentProps ?? []) {
        const val = item[prop];
        if (val && !isComponentRegistered(val)) {
          issues.push({
            message: `${p} ${check.kind} "${id}": missing component "${val}"`,
            optional: true,
          });
        }
      }
      if (check.iconProp && !isIconRegistered(item[check.iconProp] ?? '')) {
        issues.push({
          message: `${p} ${check.kind} "${id}": missing icon "${item[check.iconProp]}"`,
          optional: false,
        });
      }
      if (check.optionalIconProp) {
        const iconName = item[check.optionalIconProp];
        if (iconName && !isIconRegistered(iconName)) {
          issues.push({
            message: `${p} ${check.kind} "${id}": missing icon "${iconName}"`,
            optional: false,
          });
        }
      }
    }
  }

  // Public marketing/docs routes carry their display icon in a nested
  // `marketing.icon` / `docs.icon` field rather than the flat `iconProp`
  // the generic check above validates.
  for (const route of web.routes ?? []) {
    if (!route.public) continue;
    if (route.marketing && !isIconRegistered(route.marketing.icon)) {
      issues.push({
        message: `${p} public route "${route.path}": missing marketing icon "${route.marketing.icon}"`,
        optional: false,
      });
    }
    if (route.docs && !isIconRegistered(route.docs.icon)) {
      issues.push({
        message: `${p} public route "${route.path}": missing docs icon "${route.docs.icon}"`,
        optional: false,
      });
    }
  }

  const at = web.agentType;
  if (at) {
    if (!isIconRegistered(at.icon)) {
      issues.push({
        message: `${p} agent type "${at.type}": missing icon "${at.icon}"`,
        optional: false,
      });
    }
    if (at.vendorIcon && !isIconRegistered(at.vendorIcon)) {
      issues.push({
        message: `${p} agent type "${at.type}": missing vendor icon "${at.vendorIcon}"`,
        optional: false,
      });
    }
  }

  const adminSetup = web.adminSetupComponent;
  if (adminSetup && !isComponentRegistered(adminSetup.componentPath)) {
    issues.push({
      message: `${p} admin setup component: missing component "${adminSetup.componentPath}"`,
      optional: false,
    });
  }

  const orgSetup = web.orgSetupComponent;
  if (orgSetup && !isComponentRegistered(orgSetup.componentPath)) {
    issues.push({
      message: `${p} org setup component: missing component "${orgSetup.componentPath}"`,
      optional: false,
    });
  }

  const homePage = web.homePage;
  if (homePage && !isComponentRegistered(homePage.componentPath)) {
    issues.push({
      message: `${p} home page: missing component "${homePage.componentPath}"`,
      optional: false,
    });
  }

  return issues;
}

/**
 * Flat list of wiring-gap messages (required + optional) for admin diagnostics
 * and callers that only need display strings.
 */
export function collectPluginWebWiringIssues(plugin: PluginManifest): string[] {
  return collectPluginWebWiringIssuesDetailed(plugin).map((i) => i.message);
}

export interface ResolvedPluginRoute {
  path: string;
  component: ComponentType;
  config?: Record<string, unknown>;
  /** Whether the route opts into the Labs takeover's fullscreen mode. */
  fullscreen?: boolean;
}

/**
 * A plugin-contributed marketing page resolved for display in the
 * marketing shell's subnav and index grid.
 */
export interface ResolvedMarketingPage {
  path: string;
  label: string;
  description: string;
  icon: IconComponent;
  order: number;
  pluginName: string;
}

export interface ResolvedNavItem {
  to: string;
  label: string;
  icon: IconComponent;
  group: PluginNavItem['group'];
  /** When true, callers must not prefix `to` with the active org slug. */
  rootLevel?: boolean;
  /** When true, the sidebar renders this as an external anchor opening in a new window. */
  openInNewWindow?: boolean;
  /**
   * Plugin-contributed trailing component (e.g. a count badge) for this nav
   * item. Resolved from `PluginNavItem.badge` via the plugin's component
   * registry. The component is rendered inside a `<Suspense fallback={null}>`
   * with an optional `className`; it must return `null` when it has nothing
   * to show so the nav row stays tidy.
   */
  badge?: ComponentType<{ className?: string }>;
  /** Optional maturity stage forwarded from the plugin manifest. */
  maturityStage?: 'alpha' | 'beta' | 'preview';
  /** Display order weight (lower = higher) within a nav group. Defaults to 0. */
  order?: number;
}

export interface ResolvedCommand {
  label: string;
  to: string;
  icon: IconComponent;
  group: PluginCommand['group'];
  /** When true, callers must not prefix `to` with the active org slug. */
  rootLevel?: boolean;
}

export interface ResolvedOverviewCard {
  to: string;
  label: string;
  icon: IconComponent;
  description: string;
  details: string;
  features: string[];
  group: PluginOverviewCard['group'];
}

export interface ResolvedAgentAction {
  label: string;
  icon: IconComponent;
  to?: string;
  component?: ComponentType;
  /** Menu section when the action is pinned to the agent rail. */
  category?: 'environment' | 'persistence' | 'execution' | 'danger';
  /** Host slot. `header-before-labels` / `header-inline` are fixed chrome, not a rail pin. */
  placement?: 'rail' | 'header-before-labels' | 'header-inline';
  /** When true, the host drops this action below the `md` breakpoint. */
  hideOnMobile?: boolean;
  /** Org permission required before the host offers this action. */
  requiredPermission?: string;
  /** Whether the action also requires write access to this specific agent. */
  requiresAgentWriteAccess?: boolean;
}

export interface ResolvedAgentTab {
  id: string;
  label: string;
  icon: IconComponent;
  component: ComponentType;
  /** Devbox capability (`Agent.sandboxOptions` key) the agent must have enabled for this tab to show. */
  requiresSandboxCapability?: string;
  /** When true, the host drops this tab below the `md` breakpoint. */
  hideOnMobile?: boolean;
}

export interface ResolvedSettingsSection {
  id: string;
  label: string;
  icon: IconComponent;
  component: ComponentType;
  order: number;
}

export interface ResolvedProfileSection {
  id: string;
  label: string;
  icon: IconComponent;
  component: ComponentType;
  order: number;
}

export interface ResolvedAgentDetailSection {
  id: string;
  label: string;
  icon: IconComponent;
  component: ComponentType<{ agent: PluginAgentRef }>;
  order: number;
}

export interface ResolvedAgentHeaderBadge {
  id: string;
  component: ComponentType<{ agent: PluginAgentRef }>;
  order: number;
}

export interface HomeWidgetProps {
  userId: string | null;
}

export interface ResolvedHomeWidget {
  id: string;
  component: ComponentType<HomeWidgetProps>;
  order: number;
}

export function resolveRoutes(manifests: PluginManifest[]): ResolvedPluginRoute[] {
  return resolveManifestItems(
    manifests,
    (w) => (w.routes ?? []).filter((r) => !r.public && !r.chromeless && !r.rootLevel),
    (route, m) => {
      const component = resolveComponent(route.componentPath, `Plugin "${m.name}" route`);
      if (!component) return null;
      return {
        path: route.path,
        component,
        config: route.config,
        ...(route.fullscreen ? { fullscreen: true } : {}),
      };
    },
  );
}

/**
 * Resolves every `rootLevel: true` route across all plugin manifests.
 * These mount at the top of the SPA route tree (alongside `/profile`,
 * `/settings`, etc.) inside `AuthedShell`, instead of under `/orgs/:orgSlug/*`.
 * Use for cross-organization pages like the changelog.
 */
export function resolveRootLevelRoutes(manifests: PluginManifest[]): ResolvedPluginRoute[] {
  return resolveManifestItems(
    manifests,
    (w) => (w.routes ?? []).filter((r) => r.rootLevel && !r.public && !r.chromeless),
    (route, m) => {
      const component = resolveComponent(
        route.componentPath,
        `Plugin "${m.name}" root-level route`,
      );
      return component ? { path: route.path, component, config: route.config } : null;
    },
  );
}

/**
 * Resolves every `public: true` route across all plugin manifests that
 * targets the marketing shell. These render outside `AuthGate`/`AppShell`
 * inside the shared `MarketingLayout`. Routes carrying `docs` metadata
 * (PRD-8a) are excluded — they live in {@link resolveDocsPublicRoutes} and
 * render inside the docs-owned `DocsLayout`. Chromeless public routes are
 * also excluded — they render fully bare via {@link resolveChromelessRoutes}.
 */
export function resolvePublicRoutes(manifests: PluginManifest[]): ResolvedPluginRoute[] {
  return resolveManifestItems(
    manifests,
    (w) => (w.routes ?? []).filter((r) => r.public && !r.chromeless && !r.docs),
    (route, m) => {
      const component = resolveComponent(route.componentPath, `Plugin "${m.name}" public route`);
      return component ? { path: route.path, component, config: route.config } : null;
    },
  );
}

/**
 * Resolves every `public: true` route carrying `docs` metadata across all
 * plugin manifests (PRD-8a / ADR-002). These render outside `AuthGate`/
 * `AppShell` inside the docs-owned `DocsLayout` instead of the marketing
 * shell. Chromeless docs routes are excluded.
 */
export function resolveDocsPublicRoutes(manifests: PluginManifest[]): ResolvedPluginRoute[] {
  return resolveManifestItems(
    manifests,
    (w) => (w.routes ?? []).filter((r) => r.public && !r.chromeless && Boolean(r.docs)),
    (route, m) => {
      const component = resolveComponent(
        route.componentPath,
        `Plugin "${m.name}" public docs route`,
      );
      return component ? { path: route.path, component, config: route.config } : null;
    },
  );
}

/**
 * Resolves every `chromeless: true` route across all plugin manifests.
 * These render at the top of the route tree with no `MarketingLayout` and
 * no `AppShell`. Authed chromeless routes are still wrapped in `AuthGate`
 * by the caller; public chromeless routes render with no wrapper at all.
 */
export function resolveChromelessRoutes(manifests: PluginManifest[]): {
  authed: ResolvedPluginRoute[];
  public: ResolvedPluginRoute[];
} {
  const authed = resolveManifestItems(
    manifests,
    (w) => (w.routes ?? []).filter((r) => r.chromeless && !r.public),
    (route, m) => {
      const component = resolveComponent(
        route.componentPath,
        `Plugin "${m.name}" chromeless route`,
      );
      return component ? { path: route.path, component, config: route.config } : null;
    },
  );
  const publicRoutes = resolveManifestItems(
    manifests,
    (w) => (w.routes ?? []).filter((r) => r.chromeless && r.public),
    (route, m) => {
      const component = resolveComponent(
        route.componentPath,
        `Plugin "${m.name}" chromeless public route`,
      );
      return component ? { path: route.path, component, config: route.config } : null;
    },
  );
  return { authed, public: publicRoutes };
}

/**
 * Resolves the subset of public routes that carry marketing metadata. The
 * result is sorted by `marketing.order` (lower = earlier). Any public route
 * without marketing metadata is skipped with a console warning.
 */
export function resolveMarketingPages(manifests: PluginManifest[]): ResolvedMarketingPage[] {
  const items = resolveManifestItems(
    manifests,
    (w) => (w.routes ?? []).filter((r) => r.public && r.marketing),
    (route, m) => {
      if (!route.marketing) return null;
      const icon = resolveIcon(route.marketing.icon);
      if (!icon) return null;
      return {
        path: route.path,
        label: route.marketing.label,
        description: route.marketing.description,
        icon,
        order: route.marketing.order ?? 0,
        pluginName: m.name,
      };
    },
  );
  return [...items].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

export function resolveNavItems(manifests: PluginManifest[]): ResolvedNavItem[] {
  return resolveManifestItems(
    manifests,
    (w) => w.navItems ?? [],
    (item, m) => {
      const icon = resolveIcon(item.icon);
      if (!icon) return null;
      const resolved: ResolvedNavItem = {
        to: item.to,
        label: item.label,
        icon,
        group: item.group,
      };
      if (item.rootLevel) resolved.rootLevel = true;
      if (item.openInNewWindow) resolved.openInNewWindow = true;
      if (item.badge) {
        const badge = resolveComponent(
          item.badge,
          `Plugin "${m.name}" nav badge for "${item.label}"`,
          { quiet: true },
        );
        if (badge) {
          resolved.badge = badge as ComponentType<{ className?: string }>;
        }
      }
      if (item.maturityStage) resolved.maturityStage = item.maturityStage;
      if (item.order !== undefined) resolved.order = item.order;
      return resolved;
    },
  );
}

export function resolveCommands(manifests: PluginManifest[]): ResolvedCommand[] {
  return resolveManifestItems(
    manifests,
    (w) => w.commands ?? [],
    (cmd) => {
      const icon = resolveIcon(cmd.icon);
      if (!icon) return null;
      const resolved: ResolvedCommand = {
        label: cmd.label,
        to: cmd.to,
        icon,
        group: cmd.group,
      };
      if (cmd.rootLevel) resolved.rootLevel = true;
      return resolved;
    },
  );
}

/**
 * Fields the server may leave on an uninstalled plugin's `web` blob
 * (`filterManifestForActiveOrg`): marketing copy for the install
 * catalog, super-admin setup, and `alwaysAvailable` host-wide chrome
 * (Send feedback, etc.). None of these mean the org's integration UI
 * is wired up — routes are stripped until install.
 */
const UNINSTALLED_SURVIVING_WEB_FIELDS: ReadonlySet<keyof PluginWebManifest> = new Set([
  'overviewCards',
  'marketingPreviews',
  'adminSetupComponent',
  'navItems',
  'commands',
  'globalUiElements',
  'agentActions',
]);

const ALWAYS_AVAILABLE_ARRAY_FIELDS: ReadonlySet<keyof PluginWebManifest> = new Set([
  'navItems',
  'commands',
  'globalUiElements',
  'agentActions',
]);

/**
 * True when `web` looks like an uninstalled plugin's surviving
 * contributions: marketing fields, admin setup, and/or
 * alwaysAvailable-only arrays — but no org-active routes or other UI.
 */
function isUninstalledSurvivingWeb(manifest: PluginManifest): boolean {
  const web = manifest.web;
  if (!web) return true;
  const keys = Object.keys(web) as Array<keyof PluginWebManifest>;
  if (keys.length === 0) return true;
  for (const key of keys) {
    if (!UNINSTALLED_SURVIVING_WEB_FIELDS.has(key)) return false;
    if (ALWAYS_AVAILABLE_ARRAY_FIELDS.has(key)) {
      const items = web[key] as Array<{ alwaysAvailable?: boolean }> | undefined;
      if ((items ?? []).some((item) => item.alwaysAvailable !== true)) return false;
    }
  }
  return true;
}

export function resolveOverviewCards(manifests: PluginManifest[]): ResolvedOverviewCard[] {
  // Drop overview cards contributed by uninstalled plugins. The server
  // intentionally keeps `overviewCards` (and often `adminSetupComponent`
  // / alwaysAvailable chrome) on uninstalled plugins so the install
  // catalog can describe them, but the drill-overview pages (Plugins,
  // Integrations, Security, Misc) should only show what's actually
  // wired up — otherwise Settings links 404 on stripped routes.
  const installed = manifests.filter((m) => !isUninstalledSurvivingWeb(m));
  return resolveManifestItems(
    installed,
    (w) => w.overviewCards ?? [],
    (card) => {
      const icon = resolveIcon(card.icon);
      return icon ? { ...card, icon } : null;
    },
  );
}

export function resolveAgentTabs(manifests: PluginManifest[]): ResolvedAgentTab[] {
  return resolveManifestItems(
    manifests,
    (w) => w.agentTabs ?? [],
    (tab, m) => {
      const component = resolveComponent(tab.componentPath, `Plugin "${m.name}" agent tab`);
      if (!component) return null;
      const icon = resolveIcon(tab.icon);
      return icon
        ? {
            id: tab.id,
            label: tab.label,
            icon,
            component,
            ...(tab.requiresSandboxCapability
              ? { requiresSandboxCapability: tab.requiresSandboxCapability }
              : {}),
            ...(tab.hideOnMobile ? { hideOnMobile: true } : {}),
          }
        : null;
    },
  );
}

/**
 * Drop agent tabs whose `requiresSandboxCapability` isn't enabled on the
 * agent's `sandboxOptions` — e.g. the computer-use Desktop tab only shows
 * when the agent was launched with `computerUse` on. Tabs without the field
 * always pass. Kept pure for unit testing; `AgentStreamView` calls it with
 * the live agent's sandbox options.
 */
export function filterAgentTabsBySandboxCapabilities(
  tabs: ResolvedAgentTab[],
  sandboxOptions: Record<string, unknown> | null | undefined,
): ResolvedAgentTab[] {
  return tabs.filter(
    (tab) =>
      !tab.requiresSandboxCapability || Boolean(sandboxOptions?.[tab.requiresSandboxCapability]),
  );
}

/**
 * Drop contributions flagged `hideOnMobile` when the viewport is below `md`.
 * Desktop keeps every item. Kept pure for unit testing; the agent surfaces
 * call it via `useAgentPluginContributions`.
 */
export function filterContributionsByViewport<T extends { hideOnMobile?: boolean }>(
  items: T[],
  isDesktop: boolean,
): T[] {
  return isDesktop ? items : items.filter((item) => !item.hideOnMobile);
}

export function resolveAgentActions(manifests: PluginManifest[]): ResolvedAgentAction[] {
  return resolveManifestItems(
    manifests,
    (w) => w.agentActions ?? [],
    (action, m) => {
      const icon = resolveIcon(action.icon);
      if (!icon) return null;
      const resolved: ResolvedAgentAction = {
        label: action.label,
        icon,
        to: action.to,
        ...(action.category ? { category: action.category } : {}),
        ...(action.placement ? { placement: action.placement } : {}),
        ...(action.hideOnMobile ? { hideOnMobile: true } : {}),
        ...(action.requiredPermission ? { requiredPermission: action.requiredPermission } : {}),
        ...(action.requiresAgentWriteAccess ? { requiresAgentWriteAccess: true } : {}),
      };
      if (action.componentPath) {
        const comp = resolveComponent(action.componentPath, `Plugin "${m.name}" agent action`, {
          quiet: true,
        });
        if (!comp) return null;
        resolved.component = comp;
      }
      return resolved;
    },
  );
}

export function resolveSettingsSections(manifests: PluginManifest[]): ResolvedSettingsSection[] {
  return resolveManifestItems(
    manifests,
    (w) => w.settingsSections ?? [],
    (section, m) => {
      const component = resolveComponent(
        section.componentPath,
        `Plugin "${m.name}" settings section`,
      );
      if (!component) return null;
      const icon = resolveIcon(section.icon);
      return icon
        ? { id: section.id, label: section.label, icon, component, order: section.order ?? 0 }
        : null;
    },
  ).sort((a, b) => a.order - b.order);
}

export function resolveOrgSettingsSections(manifests: PluginManifest[]): ResolvedSettingsSection[] {
  return resolveManifestItems(
    manifests,
    (w) => w.orgSettingsSections ?? [],
    (section, m) => {
      const component = resolveComponent(
        section.componentPath,
        `Plugin "${m.name}" org settings section`,
      );
      if (!component) return null;
      const icon = resolveIcon(section.icon);
      return icon
        ? { id: section.id, label: section.label, icon, component, order: section.order ?? 0 }
        : null;
    },
  ).sort((a, b) => a.order - b.order);
}

export function resolveProfileSections(manifests: PluginManifest[]): ResolvedProfileSection[] {
  return resolveManifestItems(
    manifests,
    (w) => w.profileSections ?? [],
    (section, m) => {
      const component = resolveComponent(
        section.componentPath,
        `Plugin "${m.name}" profile section`,
      );
      if (!component) return null;
      const icon = resolveIcon(section.icon);
      return icon
        ? { id: section.id, label: section.label, icon, component, order: section.order ?? 0 }
        : null;
    },
  ).sort((a, b) => a.order - b.order);
}

export function resolveAgentDetailSections(
  manifests: PluginManifest[],
): ResolvedAgentDetailSection[] {
  return resolveManifestItems(
    manifests,
    (w) => w.agentDetailSections ?? [],
    (section, m) => {
      const component = resolveComponent(
        section.componentPath,
        `Plugin "${m.name}" agent detail section`,
      );
      if (!component) return null;
      const icon = resolveIcon(section.icon);
      if (!icon) return null;
      return {
        id: section.id,
        label: section.label,
        icon,
        component: component as unknown as ComponentType<{ agent: PluginAgentRef }>,
        order: section.order ?? 0,
      };
    },
  ).sort((a, b) => a.order - b.order);
}

/**
 * A plugin-registered chip for one agent-association kind. The component
 * renders the compact inline badge for associations of that kind; `label`
 * names the kind on list/filter surfaces (e.g. the association filter).
 */
export interface ResolvedAssociationChip {
  kind: string;
  label: string;
  component: ComponentType<AssociationChipProps>;
  pluginName: string;
}

/**
 * Resolves plugin-contributed association chips keyed by association kind.
 * The first registration wins per kind — duplicates across plugins are
 * skipped with a warning (the server's `validateAssociationKinds` already
 * hard-fails boot on kind conflicts, so this is defence-in-depth for a
 * stale client bundle). The core `pull_request` kind is rendered by the
 * built-in PR chip and never registered here.
 */
export function resolveAssociationChips(
  manifests: PluginManifest[],
): Map<string, ResolvedAssociationChip> {
  const map = new Map<string, ResolvedAssociationChip>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    for (const def of manifest.web?.associationChips ?? []) {
      if (map.has(def.kind)) {
        if (!IS_TEST_ENV) {
          console.warn(
            `[plugin-registry] associationChip for "${def.kind}" already registered; skipping plugin "${manifest.name}".`,
          );
        }
        continue;
      }
      const component = resolveComponent(
        def.componentPath,
        `Plugin "${manifest.name}" association chip`,
      );
      if (!component) continue;
      map.set(def.kind, {
        kind: def.kind,
        label: def.label,
        component: component as unknown as ComponentType<AssociationChipProps>,
        pluginName: manifest.name,
      });
    }
  }
  return map;
}

export function resolveAgentHeaderBadges(manifests: PluginManifest[]): ResolvedAgentHeaderBadge[] {
  return resolveManifestItems(
    manifests,
    (w) => w.agentHeaderBadges ?? [],
    (badge, m) => {
      const component = resolveComponent(
        badge.componentPath,
        `Plugin "${m.name}" agent header badge`,
      );
      if (!component) return null;
      return {
        id: badge.id,
        component: component as unknown as ComponentType<{ agent: PluginAgentRef }>,
        order: badge.order ?? 0,
      };
    },
  ).sort((a, b) => a.order - b.order);
}

export function resolveHomeWidgets(manifests: PluginManifest[]): ResolvedHomeWidget[] {
  return resolveManifestItems(
    manifests,
    (w) => w.homeWidgets ?? [],
    (widget, m) => {
      const component = resolveComponent(widget.componentPath, `Plugin "${m.name}" home widget`);
      if (!component) return null;
      return {
        id: widget.id,
        component: component as unknown as ComponentType<HomeWidgetProps>,
        order: widget.order ?? 0,
      };
    },
  ).sort((a, b) => a.order - b.order);
}

export interface ResolvedEventLabel {
  type: string;
  label: string;
  tooltip: string;
  style: 'system' | 'bubble';
}

export function resolveEventLabels(manifests: PluginManifest[]): Map<string, ResolvedEventLabel> {
  const map = new Map<string, ResolvedEventLabel>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    for (const el of manifest.web?.eventLabels ?? []) {
      map.set(el.type, {
        type: el.type,
        label: el.label,
        tooltip: el.tooltip,
        style: el.style ?? 'system',
      });
    }
  }
  return map;
}

/**
 * A resolved plugin user-setup step with icon component attached.
 * These steps do NOT count toward core onboarding and only surface on
 * plugin-specific pages and at the moment of failure.
 */
export interface ResolvedUserSetupStep {
  /** Owning plugin name. */
  pluginName: string;
  /** Stable identifier within the plugin. */
  id: string;
  /** Kind discriminator for UI rendering. */
  kind: 'oauth-link' | 'secret' | 'configuration' | 'action';
  /** Short UI label. */
  label: string;
  /** Longer description (optional). */
  description?: string;
  /** Route to navigate to for completing this step. */
  href: string;
  /** Resolved icon component (optional). */
  icon?: IconComponent;
}

/**
 * Resolves all plugin-contributed user setup steps into a flat array.
 * Each step is tagged with its owning plugin name for display.
 */
export function resolveUserSetupSteps(manifests: PluginManifest[]): ResolvedUserSetupStep[] {
  const steps: ResolvedUserSetupStep[] = [];
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    for (const step of manifest.web?.userSetupSteps ?? []) {
      const icon = step.icon ? iconMap[step.icon] : undefined;
      steps.push({
        pluginName: manifest.name,
        id: step.id,
        kind: step.kind,
        label: step.label,
        description: step.description,
        href: step.href,
        icon,
      });
    }
  }
  return steps;
}

export interface ResolvedAgentType {
  type: string;
  name: string;
  vendor: string;
  tagline: string;
  description: string;
  icon: IconComponent;
  vendorIcon?: IconComponent;
  capabilities: string[];
  authRequirements: { name: string; required: boolean; description: string }[];
  models: {
    id: string;
    name: string;
    default?: boolean;
    supportsImages?: boolean;
    contextWindowTokens?: number;
  }[];
  reportsContextUsage?: boolean;
  supportsSteering?: boolean;
  supportsRuntimeModelSwitch?: boolean;
  defaultContextWindowTokens?: number;
  reasoningOptions?: { id: string; name: string; default?: boolean }[];
  reasoningEnvVar?: string;
  docsUrl?: string;
  color: string;
}

export function resolveAgentTypes(manifests: PluginManifest[]): ResolvedAgentType[] {
  const types: ResolvedAgentType[] = [];
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    const at = manifest.web?.agentType;
    if (!at) continue;
    const icon = iconMap[at.icon];
    if (!icon) continue;
    const vendorIcon = at.vendorIcon ? iconMap[at.vendorIcon] : undefined;
    types.push({
      type: at.type,
      name: at.name,
      vendor: at.vendor,
      tagline: at.tagline,
      description: at.description,
      icon,
      vendorIcon,
      capabilities: at.capabilities,
      authRequirements: at.authRequirements,
      models: at.models,
      reportsContextUsage: at.reportsContextUsage,
      supportsSteering: at.supportsSteering,
      supportsRuntimeModelSwitch: at.supportsRuntimeModelSwitch,
      defaultContextWindowTokens: at.defaultContextWindowTokens,
      reasoningOptions: at.reasoningOptions,
      reasoningEnvVar: at.reasoningEnvVar,
      docsUrl: at.docsUrl,
      color: at.color,
    });
  }
  return types;
}

export interface ResolvedAttachment {
  id: string;
  label: string;
  icon: IconComponent;
  pickerComponent: ComponentType;
  /**
   * Optional compact summary component for the configured value, rendered in
   * the attachment chip. Resolved from `summaryComponentPath` when the plugin
   * provides one and it is registered.
   */
  summaryComponent?: ComponentType<AttachmentSummaryProps>;
  /** The summary renders a value-specific leading icon in place of `icon`. */
  summaryIncludesIcon: boolean;
  order: number;
  pluginName: string;
}

export function resolveAttachments(manifests: PluginManifest[]): ResolvedAttachment[] {
  return resolveManifestItems(
    manifests,
    (w) => w.attachments ?? [],
    (def, m) => {
      const component = resolveComponent(
        def.pickerComponentPath,
        `Plugin "${m.name}" attachment picker`,
      );
      if (!component) return null;
      const icon = resolveIcon(def.icon);
      if (!icon) return null;
      const summaryComponent = def.summaryComponentPath
        ? resolveComponent(def.summaryComponentPath, `Plugin "${m.name}" attachment summary`, {
            quiet: true,
          })
        : undefined;
      return {
        id: def.id,
        label: def.label,
        icon,
        pickerComponent: component,
        ...(summaryComponent
          ? {
              summaryComponent:
                summaryComponent as unknown as ComponentType<AttachmentSummaryProps>,
            }
          : {}),
        summaryIncludesIcon: def.summaryIncludesIcon ?? false,
        order: def.order ?? 0,
        pluginName: m.name,
      };
    },
  ).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

export interface ResolvedSearchProvider {
  id: string;
  label: string;
  icon: IconComponent;
  component: ComponentType<SearchProviderProps>;
  order: number;
  pluginName: string;
}

export function resolveSearchProviders(manifests: PluginManifest[]): ResolvedSearchProvider[] {
  return resolveManifestItems(
    manifests,
    (w) => w.searchProviders ?? [],
    (def, m) => {
      const component = resolveComponent(def.componentPath, `Plugin "${m.name}" search provider`);
      if (!component) return null;
      const icon = resolveIcon(def.icon);
      if (!icon) return null;
      return {
        id: def.id,
        label: def.label,
        icon,
        component: component as unknown as ComponentType<SearchProviderProps>,
        order: def.order ?? 0,
        pluginName: m.name,
      };
    },
  ).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

export interface ResolvedLaunchMentionProvider {
  id: string;
  label: string;
  /**
   * Text slug that scopes the menu to this category via `@<keyword>:`. Lowercase
   * and unique across providers; undefined when the category has no typed scope.
   */
  keyword?: string;
  icon: IconComponent;
  component: ComponentType<LaunchMentionProviderProps>;
  /**
   * Label for the inline "Add new …" row (e.g. 'Add MCP server'), present only
   * when the provider also ships a {@link createComponent}.
   */
  createLabel?: string;
  /**
   * Optional inline "Add new …" create surface for this category. Undefined for
   * categories where creation makes no sense (e.g. git repos).
   */
  createComponent?: ComponentType<LaunchMentionCreateProps>;
  /**
   * Optional inline "Edit …" surface for a row in this category, opened from
   * the row's cog. Undefined for categories whose items are not editable in
   * place (e.g. git repos, agent runs).
   */
  editComponent?: ComponentType<LaunchMentionEditProps>;
  /**
   * Whether this category also appears in a running agent's `@` menu, where
   * its `set-attachment` results become a live attach against the run. See
   * `PluginLaunchMentionProvider.runtimeAttachable`.
   */
  runtimeAttachable: boolean;
  order: number;
  pluginName: string;
}

/**
 * Resolves plugin-contributed providers for the agent launch `@`-mention menu.
 * Mirrors {@link resolveSearchProviders}: each provider component is rendered
 * inside its own `CommandGroup` and receives `LaunchMentionProviderProps`.
 * Sorted by `order` (lower = earlier), then label. Missing components/icons are
 * silently dropped — the wiring-issue collector reports them separately.
 */
export function resolveLaunchMentionProviders(
  manifests: PluginManifest[],
): ResolvedLaunchMentionProvider[] {
  return resolveManifestItems(
    manifests,
    (w) => w.launchMentionProviders ?? [],
    (def, m) => {
      const component = resolveComponent(
        def.componentPath,
        `Plugin "${m.name}" launch mention provider`,
      );
      if (!component) return null;
      const icon = resolveIcon(def.icon);
      if (!icon) return null;
      // An inline "Add new …" create surface is optional: resolve it only when
      // the provider declares one and the component is actually wired. A
      // declared-but-missing create component just drops the add row (the
      // wiring-issue collector reports it separately) without hiding the whole
      // category.
      const createComponent = def.createComponentPath
        ? resolveComponent(def.createComponentPath, `Plugin "${m.name}" launch mention create`, {
            quiet: true,
          })
        : undefined;
      // Same optional treatment for the row-level "Edit …" surface: a missing
      // component drops the cog, never the category.
      const editComponent = def.editComponentPath
        ? resolveComponent(def.editComponentPath, `Plugin "${m.name}" launch mention edit`, {
            quiet: true,
          })
        : undefined;
      return {
        id: def.id,
        label: def.label,
        keyword: def.keyword,
        icon,
        component: component as unknown as ComponentType<LaunchMentionProviderProps>,
        createLabel: createComponent ? def.createLabel : undefined,
        createComponent: createComponent as ComponentType<LaunchMentionCreateProps> | undefined,
        editComponent: editComponent as ComponentType<LaunchMentionEditProps> | undefined,
        runtimeAttachable: def.runtimeAttachable ?? false,
        order: def.order ?? 0,
        pluginName: m.name,
      };
    },
  ).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

export interface ResolvedAgentContextProvider {
  id: string;
  /** The context reference kind this provider emits (e.g. `github-pr`). */
  kind: string;
  label: string;
  /**
   * Text slug that scopes the menu to this category via `@<keyword>:`.
   * Lowercase and unique across providers; undefined when the category has no
   * typed scope.
   */
  keyword?: string;
  icon: IconComponent;
  component: ComponentType<AgentContextProviderProps>;
  order: number;
  pluginName: string;
}

/**
 * Resolves plugin-contributed agent-context categories for the `@`-mention
 * menu (both the launch composer and a running agent's chat). Mirrors
 * {@link resolveLaunchMentionProviders}: each provider component renders inside
 * its own `CommandGroup` and receives `AgentContextProviderProps`. Sorted by
 * `order` (lower = earlier), then label. Missing components/icons are silently
 * dropped — the wiring-issue collector reports them separately.
 */
export function resolveAgentContextProviders(
  manifests: PluginManifest[],
): ResolvedAgentContextProvider[] {
  return resolveManifestItems(
    manifests,
    (w) => w.agentContextProviders ?? [],
    (def, m) => {
      // A kind the marker builder rejects would crash chip serialization on
      // submit; hide the category instead (definePlugin already warns).
      if (!CONTEXT_REFERENCE_KIND_PATTERN.test(def.kind) || def.kind === 'agent-run') {
        console.warn(
          `[plugin-registry] Plugin "${m.name}" agent context provider "${def.id}" has invalid kind "${def.kind}"; dropping the category`,
        );
        return null;
      }
      const component = resolveComponent(
        def.componentPath,
        `Plugin "${m.name}" agent context provider`,
      );
      if (!component) return null;
      const icon = resolveIcon(def.icon);
      if (!icon) return null;
      return {
        id: def.id,
        kind: def.kind,
        label: def.label,
        keyword: def.keyword,
        icon,
        component: component as unknown as ComponentType<AgentContextProviderProps>,
        order: def.order ?? 0,
        pluginName: m.name,
      };
    },
  ).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

export interface ResolvedChatMentionProvider {
  id: string;
  label: string;
  icon: IconComponent;
  component: ComponentType<ChatMentionProviderProps>;
  order: number;
  pluginName: string;
}

/**
 * Resolves plugin-contributed *actions* for a running agent's `@`-mention
 * menu — the sibling of {@link resolveAgentContextProviders}. Those reference
 * an external item; these act on the agent already running, so the component
 * receives `ChatMentionProviderProps` (with `agentId`) and emits a `custom`
 * result. Sorted by `order` (lower = earlier), then label.
 */
export function resolveChatMentionProviders(
  manifests: PluginManifest[],
): ResolvedChatMentionProvider[] {
  return resolveManifestItems(
    manifests,
    (w) => w.chatMentionProviders ?? [],
    (def, m) => {
      const component = resolveComponent(
        def.componentPath,
        `Plugin "${m.name}" chat mention provider`,
      );
      if (!component) return null;
      const icon = resolveIcon(def.icon);
      if (!icon) return null;
      return {
        id: def.id,
        label: def.label,
        icon,
        component: component as unknown as ComponentType<ChatMentionProviderProps>,
        order: def.order ?? 0,
        pluginName: m.name,
      };
    },
  ).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

export interface ResolvedMarketingPreview {
  id: string;
  pluginName: string;
  label: string;
  description?: string;
  surface: PluginMarketingPreviewSurface;
  component: ComponentType;
  order: number;
}

/**
 * Resolves opt-in marketing preview components contributed by every
 * enabled plugin. Each preview is a self-contained React component that
 * renders sample data so the org plugin detail page can show users what
 * the plugin's UI actually looks like before they install it.
 *
 * Sorted by `order` (lower = earlier), then alphabetically by plugin name
 * + preview id so the order is deterministic across renders. Missing
 * components are silently dropped — the wiring-issue collector reports
 * them separately.
 */
export function resolveMarketingPreviews(manifests: PluginManifest[]): ResolvedMarketingPreview[] {
  return resolveManifestItems(
    manifests,
    (w) => w.marketingPreviews ?? [],
    (def, m) => {
      const component = resolveComponent(def.componentPath, `Plugin "${m.name}" marketing preview`);
      if (!component) return null;
      return {
        id: def.id,
        pluginName: m.name,
        label: def.label,
        ...(def.description ? { description: def.description } : {}),
        surface: def.surface,
        component,
        order: def.order ?? 0,
      };
    },
  ).sort(
    (a, b) =>
      a.order - b.order || a.pluginName.localeCompare(b.pluginName) || a.id.localeCompare(b.id),
  );
}

export interface ResolvedGlobalUiElement {
  id: string;
  pluginName: string;
  component: ComponentType;
}

/**
 * Resolves UI elements mounted globally inside `AppShell` for every
 * enabled plugin. Components render with no props and must short-circuit
 * to `null` when they have nothing to show — the host always mounts them.
 */
/**
 * Org-relative paths of plugin routes that stay reachable while the active
 * org is disabled for `reason` (`availableWhenOrgDisabled` on the route).
 * `OrgGate` lets the current location through when it falls under one of
 * these, and the blocking screens use the first entry as their cure-path
 * CTA (the billing page for `nonpayment` / `subscription_expired`).
 */
export function resolveOrgDisabledCurePaths(
  manifests: PluginManifest[],
  reason: OrganizationDisabledReason,
): string[] {
  const paths: string[] = [];
  for (const manifest of manifests) {
    for (const route of manifest.web?.routes ?? []) {
      if (route.availableWhenOrgDisabled?.includes(reason)) {
        paths.push(route.path);
      }
    }
  }
  return paths;
}

export function resolveGlobalUiElements(manifests: PluginManifest[]): ResolvedGlobalUiElement[] {
  return resolveManifestItems(
    manifests,
    (w) => w.globalUiElements ?? [],
    (def, m) => {
      const component = resolveComponent(def.componentPath, `Plugin "${m.name}" global UI element`);
      if (!component) return null;
      return { id: def.id, pluginName: m.name, component };
    },
  );
}

export interface ResolvedNavUtilityElement {
  id: string;
  pluginName: string;
  component: ComponentType<PluginNavUtilityProps>;
  order: number;
}

/**
 * Resolves the icon-sized controls plugins mount in the nav's utility row,
 * beside the Inbox button. Components receive {@link PluginNavUtilityProps}
 * and must short-circuit to `null` when they have nothing to show — the
 * host always mounts them.
 *
 * Sorted by `order` (lower = nearer the Inbox button), then by plugin name
 * + element id so the row is stable across renders.
 */
export function resolveNavUtilityElements(
  manifests: PluginManifest[],
): ResolvedNavUtilityElement[] {
  return resolveManifestItems(
    manifests,
    (w) => w.navUtilityElements ?? [],
    (def, m) => {
      const component = resolveComponent(def.componentPath, `Plugin "${m.name}" nav utility`);
      if (!component) return null;
      return {
        id: def.id,
        pluginName: m.name,
        component: component as unknown as ComponentType<PluginNavUtilityProps>,
        order: def.order ?? 0,
      };
    },
  ).sort(
    (a, b) =>
      a.order - b.order || a.pluginName.localeCompare(b.pluginName) || a.id.localeCompare(b.id),
  );
}

export interface ResolvedAdminUserAction {
  id: string;
  pluginName: string;
  component: ComponentType<AdminUserActionProps>;
  order: number;
}

/**
 * Resolves plugin-contributed action components rendered on the admin
 * users surfaces (`/admin/users` row actions, `/admin/users/:userId`
 * header actions). Each component receives `AdminUserActionProps` and
 * is expected to render its own button + any required dialog state.
 *
 * Sorted by `order` (lower = first), then alphabetically by plugin
 * name + action id so the order is deterministic across renders.
 */
export function resolveAdminUserActions(manifests: PluginManifest[]): ResolvedAdminUserAction[] {
  return resolveManifestItems(
    manifests,
    (w) => w.adminUserActions ?? [],
    (def, m) => {
      const component = resolveComponent(def.componentPath, `Plugin "${m.name}" admin user action`);
      if (!component) return null;
      return {
        id: def.id,
        pluginName: m.name,
        component: component as unknown as ComponentType<AdminUserActionProps>,
        order: def.order ?? 0,
      };
    },
  ).sort(
    (a, b) =>
      a.order - b.order || a.pluginName.localeCompare(b.pluginName) || a.id.localeCompare(b.id),
  );
}

export interface ResolvedFlowTriggerRenderer {
  triggerType: string;
  label: string;
  icon: IconComponent;
  component: ComponentType<FlowTriggerRendererProps>;
  pluginName: string;
}

/**
 * A plugin-contributed tab on another package's page, resolved into runtime
 * pieces. The hosting page owns the chrome (header, segmented strip, URL
 * state); `component` renders the tab body.
 */
export interface ResolvedPageTab {
  id: string;
  label: string;
  icon: IconComponent;
  component: ComponentType;
  order: number;
  pluginName: string;
}

export interface ResolvedToolCallRenderer {
  toolName: string;
  label: string;
  labelInProgress?: string;
  labelFailed?: string;
  icon: IconComponent;
  component: ComponentType<ToolCallRendererProps>;
  pluginName: string;
}

/**
 * A plugin-contributed resource preview registration resolved into runtime
 * pieces. The `component` renders the body of the popover/hover-card; the
 * shared shell (`ResourcePreviewCard`) owns the surrounding chrome.
 */
export interface ResolvedResourcePreview {
  kind: string;
  label: string;
  icon: IconComponent;
  component: ComponentType<ResourcePreviewProps>;
  detailHref?: string;
  pluginName: string;
  /** 3-letter id prefixes this preview claims (drives the entity-id badge). */
  idPrefixes?: string[];
}

/**
 * Resolves plugin-contributed resource preview components keyed by `kind`.
 * The first registration wins per kind — duplicates across plugins are
 * skipped with a warning. Used by `<ResourceChip>` and `<ResourceRowTrigger>`
 * to render a consistent popover for any entity in the app.
 */
export function resolveResourcePreviews(
  manifests: PluginManifest[],
): Map<string, ResolvedResourcePreview> {
  const map = new Map<string, ResolvedResourcePreview>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    const previews = manifest.web?.resourcePreviews ?? [];
    for (const def of previews) {
      if (map.has(def.kind)) {
        if (!IS_TEST_ENV) {
          console.warn(
            `[plugin-registry] resourcePreview for "${def.kind}" already registered; skipping plugin "${manifest.name}".`,
          );
        }
        continue;
      }
      const component = resolveComponent(
        def.componentPath,
        `Plugin "${manifest.name}" resource preview`,
      );
      if (!component) continue;
      const icon = resolveIcon(def.icon);
      if (!icon) continue;
      map.set(def.kind, {
        kind: def.kind,
        label: def.label,
        icon,
        component: component as unknown as ComponentType<ResourcePreviewProps>,
        ...(def.detailHref ? { detailHref: def.detailHref } : {}),
        ...(def.idPrefixes?.length ? { idPrefixes: def.idPrefixes } : {}),
        pluginName: manifest.name,
      });
    }
  }
  return map;
}

/**
 * Indexes resolved resource previews by the 3-letter id prefixes they claim
 * (`resourcePreviews[].idPrefixes`). Lets the entity-id badge resolve a raw id
 * to its preview + icon via the prefix. First registration wins per prefix; a
 * later preview claiming the same prefix is skipped with a warning. Core
 * prefixes without a plugin owner fall back to the built-in registry (see
 * `getBuiltInEntityIdPreview`).
 */
export function resolveEntityIdPreviews(
  resourcePreviews: Map<string, ResolvedResourcePreview>,
): Map<string, ResolvedResourcePreview> {
  const byPrefix = new Map<string, ResolvedResourcePreview>();
  for (const preview of resourcePreviews.values()) {
    for (const prefix of preview.idPrefixes ?? []) {
      if (byPrefix.has(prefix)) {
        if (!IS_TEST_ENV) {
          console.warn(
            `[plugin-registry] entity-id preview for prefix "${prefix}" already registered; skipping "${preview.pluginName}".`,
          );
        }
        continue;
      }
      byPrefix.set(prefix, preview);
    }
  }
  return byPrefix;
}

export interface ResolvedOwnerKind {
  idPrefix: string;
  kind: string;
  label: string;
  icon: IconComponent;
  pluginName: string;
}

/**
 * Resolves plugin-contributed owner-kind display metadata keyed by
 * `idPrefix`. The first registration wins per prefix — duplicates
 * across plugins are skipped with a warning. Used by `<OwnerChip>` and
 * `useResolvedOwner` to render a plugin-owned owner kind (`prj_*`,
 * future kinds) with the right icon and label instead of the host
 * fallback.
 */
/**
 * Every org-level sharing setting the installed plugins register, in
 * display order. Pure data — the host's Sharing settings page owns the
 * switch, the clamp against the org's sharing ceiling, and the write — so
 * unlike most manifest contributions there is no component to resolve.
 * Uninstalled plugins are already stripped server-side, so an org only
 * sees settings that can affect it. A key two plugins somehow claim is
 * kept once (the server's registration refuses the second declaration at
 * boot; this is the client-side echo of that rule).
 */
export function resolveSessionSharingSettings(
  manifests: PluginManifest[],
): SessionSharingSetting[] {
  const byKey = new Map<string, SessionSharingSetting>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    for (const setting of manifest.web?.sessionSharingSettings ?? []) {
      if (byKey.has(setting.key)) {
        if (!IS_TEST_ENV) {
          console.warn(
            `[plugin-registry] sharing setting "${setting.key}" already registered; skipping plugin "${manifest.name}".`,
          );
        }
        continue;
      }
      byKey.set(setting.key, setting);
    }
  }
  return [...byKey.values()].sort((a, b) => a.order - b.order);
}

export function resolveOwnerKinds(manifests: PluginManifest[]): Map<string, ResolvedOwnerKind> {
  const map = new Map<string, ResolvedOwnerKind>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    const entries = manifest.web?.ownerKinds ?? [];
    for (const def of entries) {
      if (map.has(def.idPrefix)) {
        if (!IS_TEST_ENV) {
          console.warn(
            `[plugin-registry] ownerKind for prefix "${def.idPrefix}" already registered; skipping plugin "${manifest.name}".`,
          );
        }
        continue;
      }
      const icon = resolveIcon(def.icon);
      if (!icon) continue;
      map.set(def.idPrefix, {
        idPrefix: def.idPrefix,
        kind: def.kind,
        label: def.label,
        icon,
        pluginName: manifest.name,
      });
    }
  }
  return map;
}

export interface ResolvedOriginatorKind {
  idPrefix: string;
  label: string;
  pluginName: string;
}

/**
 * Resolves plugin-contributed agent-originator kinds keyed by `idPrefix`. The
 * first registration wins per prefix — duplicates across plugins are skipped
 * with a warning. The agent list's originator/type filter merges these with
 * the built-in `agent` and `flow` kinds so it can offer plugin-launched
 * originators (e.g. Jira `jrr_*`) as filterable types. Icon-free: unlike
 * owner kinds these are label-only, so no icon resolution is needed.
 */
export function resolveOriginators(
  manifests: PluginManifest[],
): Map<string, ResolvedOriginatorKind> {
  const map = new Map<string, ResolvedOriginatorKind>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    const entries = manifest.web?.originators ?? [];
    for (const def of entries) {
      if (map.has(def.idPrefix)) {
        if (!IS_TEST_ENV) {
          console.warn(
            `[plugin-registry] originator for prefix "${def.idPrefix}" already registered; skipping plugin "${manifest.name}".`,
          );
        }
        continue;
      }
      map.set(def.idPrefix, {
        idPrefix: def.idPrefix,
        label: def.label,
        pluginName: manifest.name,
      });
    }
  }
  return map;
}

/**
 * Resolves plugin-contributed renderers for flow trigger payloads (e.g.
 * Sentry, Linear, GitHub PR). The first registration wins per `triggerType`
 * — duplicate registrations across plugins are skipped with a warning.
 */
export function resolveFlowTriggerRenderers(
  manifests: PluginManifest[],
): Map<string, ResolvedFlowTriggerRenderer> {
  const map = new Map<string, ResolvedFlowTriggerRenderer>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    const renderers = manifest.web?.flowTriggerRenderers ?? [];
    for (const def of renderers) {
      if (map.has(def.triggerType)) {
        if (!IS_TEST_ENV) {
          console.warn(
            `[plugin-registry] flowTriggerRenderer for "${def.triggerType}" already registered; skipping plugin "${manifest.name}".`,
          );
        }
        continue;
      }
      const component = resolveComponent(
        def.componentPath,
        `Plugin "${manifest.name}" automation trigger renderer`,
      );
      if (!component) continue;
      const icon = resolveIcon(def.icon);
      if (!icon) continue;
      map.set(def.triggerType, {
        triggerType: def.triggerType,
        label: def.label,
        icon,
        component: component as unknown as ComponentType<FlowTriggerRendererProps>,
        pluginName: manifest.name,
      });
    }
  }
  return map;
}

/**
 * Resolves plugin-contributed page tabs, grouped by the hosting page's slot
 * id and ordered within each slot (`order`, then plugin name, then tab id —
 * the same tiebreak chain the other ordered contributions use, so the strip
 * is stable regardless of manifest load order). The first registration wins
 * per `slot`/`id` pair; duplicates across plugins are skipped with a
 * warning, matching `flowTriggerRenderers`.
 */
export function resolvePageTabs(manifests: PluginManifest[]): Map<string, ResolvedPageTab[]> {
  const bySlot = new Map<string, ResolvedPageTab[]>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    for (const def of manifest.web?.pageTabs ?? []) {
      const tabs = bySlot.get(def.slot) ?? [];
      if (tabs.some((t) => t.id === def.id)) {
        if (!IS_TEST_ENV) {
          console.warn(
            `[plugin-registry] pageTab "${def.id}" for slot "${def.slot}" already registered; skipping plugin "${manifest.name}".`,
          );
        }
        continue;
      }
      const component = resolveComponent(def.componentPath, `Plugin "${manifest.name}" page tab`);
      if (!component) continue;
      const icon = resolveIcon(def.icon);
      if (!icon) continue;
      tabs.push({
        id: def.id,
        label: def.label,
        icon,
        component: component as unknown as ComponentType,
        order: def.order ?? 0,
        pluginName: manifest.name,
      });
      bySlot.set(def.slot, tabs);
    }
  }
  for (const tabs of bySlot.values()) {
    tabs.sort(
      (a, b) =>
        a.order - b.order || a.pluginName.localeCompare(b.pluginName) || a.id.localeCompare(b.id),
    );
  }
  return bySlot;
}

/**
 * Resolves plugin-contributed renderers for MCP tool calls in the agent
 * chat (e.g. workstation run/read/write/list). Keyed by normalized tool
 * name; the first registration wins — duplicates across plugins are
 * skipped with a warning.
 */
export function resolveToolCallRenderers(
  manifests: PluginManifest[],
): Map<string, ResolvedToolCallRenderer> {
  const map = new Map<string, ResolvedToolCallRenderer>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    const renderers = manifest.web?.toolCallRenderers ?? [];
    for (const def of renderers) {
      if (map.has(def.toolName)) {
        if (!IS_TEST_ENV) {
          console.warn(
            `[plugin-registry] toolCallRenderer for "${def.toolName}" already registered; skipping plugin "${manifest.name}".`,
          );
        }
        continue;
      }
      const component = resolveComponent(
        def.componentPath,
        `Plugin "${manifest.name}" tool call renderer`,
      );
      if (!component) continue;
      const icon = resolveIcon(def.icon);
      if (!icon) continue;
      map.set(def.toolName, {
        toolName: def.toolName,
        label: def.label,
        labelInProgress: def.labelInProgress,
        labelFailed: def.labelFailed,
        icon,
        component: component as unknown as ComponentType<ToolCallRendererProps>,
        pluginName: manifest.name,
      });
    }
  }
  return map;
}
